import { Controller } from "@hotwired/stimulus"
import CalHeatMap from "cal-heatmap"
import Legend from "cal-heatmap/plugins/Legend"
import CalendarLabel from "cal-heatmap/plugins/CalendarLabel"
import Tooltip from "cal-heatmap/plugins/Tooltip"

// Employee-selecting filter keys the heatmap honors. Forwarded to the JSON endpoint (which scopes
// the "missing" counts) and to the drill-down submit (so the listed people match the clicked count).
const FILTER_KEYS = [
  "q[employee_id_in][]",
  "q[team_id_in][]",
  "q[trade_id_in][]",
  "q[employee_type_id_in][]",
  "filter_submitted",
]

// The heatmap only depends on the filter keys above, so a frame load that changed none of them (tab,
// page, sort, search) reuses what is painted — unless it is older than this, so entries created
// meanwhile still show up.
const MAX_DATA_AGE_MS = 60_000

export default class extends Controller {
  static targets = ["calHeatmapContainer", "form"]

  connect() {
    this.cal = null
    this.renderedKey = null
    this.renderedAt = 0
    this.abortController = null
    // The heatmap lives in the sidebar, OUTSIDE the #configurable_timesheet_content turbo frame, so
    // its connect() never re-fires when the list is filtered. Re-render on the frame's load instead.
    this.onFrameLoad = (event) => {
      if (event.target && event.target.id === "configurable_timesheet_content") {
        this.render()
      }
    }
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.render()
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    this.abortController?.abort()
    this.destroyCalendar()
  }

  // cal-heatmap's destroy() animates the old SVG away; the new one would be appended below it
  // meanwhile and flash. Detach the old markup right away and let the animation run on nothing.
  destroyCalendar() {
    if (!this.cal) return

    this.cal.destroy().catch(() => {})
    this.cal = null
    document.getElementById("configurable-cal-heatmap")?.replaceChildren()
    document.getElementById("configurable-cal-heatmap-legend")?.replaceChildren()
  }

  filterKey(params) {
    return FILTER_KEYS.map((key) => `${key}=${params.getAll(key).join(",")}`).join("&")
  }

  // The active filter, read from the content frame's src (Turbo sets it to the navigated URL on a
  // filter/tab/search change) and falling back to the page URL on the initial load.
  currentFilterParams() {
    const frame = document.getElementById("configurable_timesheet_content")
    let search = window.location.search
    if (frame && frame.src) {
      try {
        search = new URL(frame.src).search
      } catch {
        // keep window.location.search
      }
    }
    return new URLSearchParams(search)
  }

  async render() {
    const calHeatmapElement = document.getElementById("configurable-cal-heatmap")
    if (!calHeatmapElement) return

    const params = this.currentFilterParams()
    const key = this.filterKey(params)
    const fresh = Date.now() - this.renderedAt < MAX_DATA_AGE_MS

    // A newer render supersedes any pending one: fast tab clicks must not let a slow, stale response
    // paint over the current filter's heatmap. Abort before the reuse check too, or switching A → B → A
    // keeps A painted while B's still-pending response lands on top of it.
    this.abortController?.abort()
    this.abortController = null
    if (this.cal && key === this.renderedKey && fresh) return

    const abortController = new AbortController()
    this.abortController = abortController

    const today = new Date()
    const previousMonth = new Date(today.getFullYear(), today.getMonth(), 1)

    // Pre-fetch data with error handling to prevent unhandled Promise rejections
    // This fixes Firefox "Content-Length header exceeds response Body" errors
    let employeesCount = 0
    let days = []
    let loaded = false
    try {
      const query = params.toString()
      const response = await fetch(`/configurable_time_sheets.json${query ? `?${query}` : ""}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        signal: abortController.signal,
      })
      if (response.ok) {
        const payload = await response.json()
        days = payload.days || []
        if (payload.employees_count != null) employeesCount = payload.employees_count
        loaded = true
      } else {
        console.warn("Failed to load calendar heatmap data:", response.status)
      }
    } catch (error) {
      if (error.name === "AbortError") return
      // Gracefully handle network errors (truncated responses, timeouts, etc.)
      console.warn("Error loading calendar heatmap data:", error.message)
    }
    if (abortController.signal.aborted) return

    // Repaint from scratch so a filter change replaces the previous squares instead of stacking SVGs.
    this.destroyCalendar()
    // A failed load paints the empty calendar but is retried on the next frame load.
    this.renderedKey = loaded ? key : null
    this.renderedAt = Date.now()

    const cal = new CalHeatMap()
    this.cal = cal
    const scale = {
      color: {
        range: ["#0ecc6d", "#ec2a2a"],
        domain: [0, employeesCount],
      },
    }

    cal.paint(
      {
        data: {
          source: days,
          x: "date",
          y: "value",
          type: "json",
        },
        date: {
          weekStart: 1,
          start: previousMonth,
          locale: "de",
        },
        scale: scale,
        range: 2,
        animationDuration: 0,
        itemSelector: "#configurable-cal-heatmap",
        domain: {
          type: "month",
          label: { position: "top" },
        },
        subDomain: {
          radius: 2,
          type: "day",
          width: 18,
          height: 18,
          padding: [0, 0, 0, 0],
          color: (_timestamp, _value, _backgroundColor) => {
            return "black"
          },
        },
      },
      [
        [
          Tooltip,
          {
            text: function (date, value, dayjsDate) {
              return (
                (value ? value + "/" + employeesCount : "Keine Stundenzettel") +
                " fehlen am " +
                dayjsDate.format("LL")
              )
            },
          },
        ],
        [
          Legend,
          {
            itemSelector: "#configurable-cal-heatmap-legend",
            label: null,
            width: 120,
          },
        ],
        [
          CalendarLabel,
          {
            position: "left",
            key: "left",
            text: () => ["", "Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
            textAlign: "start",
            width: 30,
          },
        ],
      ],
    )

    cal.on("click", (data, timestamp, _value) => {
      const date = new Date(timestamp).toISOString().split("T")[0]
      const form = document.querySelector("[data-configurable-calendar-heatmap-target='form']")
      if (form) {
        // The calendar form is static (sidebar, not re-rendered), so clear fields from a prior click.
        form.querySelectorAll("[data-heatmap-injected]").forEach((field) => field.remove())
        this.appendHiddenField(form, "date", date)
        // Carry the active employee filter so the drill-down list matches the clicked count.
        const filterParams = this.currentFilterParams()
        FILTER_KEYS.forEach((key) => {
          filterParams.getAll(key).forEach((value) => {
            if (value !== "") this.appendHiddenField(form, key, value)
          })
        })
        form.requestSubmit()
      }
    })
  }

  appendHiddenField(form, name, value) {
    const hiddenField = document.createElement("input")
    hiddenField.type = "hidden"
    hiddenField.name = name
    hiddenField.value = value
    hiddenField.setAttribute("data-heatmap-injected", "")
    form.appendChild(hiddenField)
  }
}
